import {useOrderChangeContext} from '@hconnect/tuc-processes/orderManagement/orderChange'
import {Button} from '@hm/fundamental'
import {Close} from '@mui/icons-material'
import {Box, Typography, IconButton} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {OrderConfirmationSection} from '../../../../shared/order-editor/components/fields/Confirm/OrderConfirmationSection'
import {useOrderConfirmationUIState} from '../../../hooks/useOrderConfirmationUIState'
import {useOrderManagementFieldConfigurations} from '../../../hooks/useOrderManagementFieldConfigurations'

import {FieldButtonWithState} from './FieldButtonWithState'

interface FieldEditorOverviewProps {
  onFieldClick: (fieldId: string) => void
  onClose?: () => void
  onFinalReview?: () => void
  jobSiteInfo?: string
}

/**
 * FieldEditorOverview Component
 *
 * Displays a list of all enabled field buttons for order management.
 * Each button opens the corresponding field editor when clicked.
 *
 * This component uses the configuration system to dynamically render
 * only the fields that are enabled based on region features and
 * field configuration context.
 */
export const FieldEditorOverview: React.FC<FieldEditorOverviewProps> = ({
  onFieldClick,
  onClose,
  onFinalReview,
  jobSiteInfo
}) => {
  const {t} = useTranslation()
  const fieldConfigurations = useOrderManagementFieldConfigurations()
  const confirmationUIState = useOrderConfirmationUIState()
  const {canSubmitChanges, processData: order} = useOrderChangeContext()

  return (
    <Box p={3} height="100%">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px'
        }}
        data-test-id="field-editor-overview"
      >
        {/* Header */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 600,
                lineHeight: '24px',
                color: '#29333d',
                fontFamily: 'Inter'
              }}
            >
              {t('orderIntake.rmc.orderManagement.editOrder', 'Edit order')}
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{
                padding: '4px',
                color: '#52667a'
              }}
              data-test-id="close-order-edit-button"
            >
              <Close sx={{fontSize: '16px'}} />
            </IconButton>
          </Box>
          {jobSiteInfo && (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '16px',
                color: '#52667a',
                fontFamily: 'Inter'
              }}
            >
              {jobSiteInfo}
            </Typography>
          )}
        </Box>

        {/* User Order Confirmation Field */}
        {confirmationUIState.type !== 'hidden' && (
          <OrderConfirmationSection
            disabled={confirmationUIState.type === 'disabled'}
            disabledMessage={
              'disabledMessage' in confirmationUIState
                ? confirmationUIState.disabledMessage
                : undefined
            }
            isPinRequired={
              'variant' in confirmationUIState && confirmationUIState.variant === 'pin-required'
            }
            order={order}
          />
        )}

        {/* Field Buttons */}
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
          {fieldConfigurations.map((config) => (
            <FieldButtonWithState
              key={config.fieldId}
              config={config}
              onFieldClick={onFieldClick}
            />
          ))}
        </Box>

        {/* Action Buttons */}
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
          {onFinalReview && (
            <Button
              variant="primary"
              size="large"
              onClick={onFinalReview}
              disabled={!canSubmitChanges}
              className="w-full"
              data-test-id="final-review-button"
            >
              {t('orderIntake.rmc.orderManagement.finalReview', 'Final Review')}
            </Button>
          )}
          <Button
            variant="secondary"
            size="large"
            onClick={onClose}
            className="w-full"
            data-test-id="discard-and-close-button"
          >
            {t('orderIntake.rmc.orderManagement.discardAndClose', 'Discard and Close')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
